import { getNotificationEventLabel } from "@/features/notifications/constants/event-labels";
import { formatOptionalArabicDateTime } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import type { UserNotification } from "@/types/notification";

function formatRelativeTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const diffMs = date.getTime() - Date.now();
  const diffMinutes = Math.round(diffMs / 60_000);
  const rtf = new Intl.RelativeTimeFormat("ar", { numeric: "auto" });
  if (Math.abs(diffMinutes) < 60) return rtf.format(diffMinutes, "minute");
  const diffHours = Math.round(diffMinutes / 60);
  if (Math.abs(diffHours) < 24) return rtf.format(diffHours, "hour");
  const diffDays = Math.round(diffHours / 24);
  if (Math.abs(diffDays) < 7) return rtf.format(diffDays, "day");
  return formatOptionalArabicDateTime(iso);
}

interface NotificationListProps {
  items: UserNotification[];
  onSelect: (notification: UserNotification) => void;
  compact?: boolean;
  emptyMessage?: string;
}

export function NotificationList({
  items,
  onSelect,
  compact = false,
  emptyMessage = "لا توجد إشعارات",
}: NotificationListProps) {
  if (items.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">{emptyMessage}</p>
    );
  }

  return (
    <ul className={cn("divide-y divide-border", compact ? "" : "rounded-lg border border-border")}>
      {items.map((notification) => {
        const unread = !notification.readAt;
        return (
          <li key={notification.id}>
            <button
              type="button"
              onClick={() => onSelect(notification)}
              className={cn(
                "flex w-full items-start gap-3 px-3 py-3 text-right transition-colors hover:bg-muted/60",
                compact ? "px-2" : "px-4",
                unread && "bg-primary/5",
              )}
            >
              <span
                className={cn(
                  "mt-1.5 size-2 shrink-0 rounded-full",
                  unread ? "bg-primary" : "bg-transparent",
                )}
                aria-hidden
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-foreground">
                  {notification.title}
                </span>
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  {getNotificationEventLabel(notification.templateKey)}
                </span>
                {!compact && (
                  <span className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                    {notification.body}
                  </span>
                )}
              </span>
              <time
                dateTime={notification.createdAt}
                className="shrink-0 text-[10px] text-muted-foreground"
              >
                {formatRelativeTime(notification.createdAt)}
              </time>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
